import { Component, HostListener, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { BsModalService, BsModalRef } from 'ngx-bootstrap/modal';
import { PopoverDirective } from 'ngx-bootstrap/popover';
import { BaseModalComponent } from '../../../models/base-modal-component';
import { CifsAuthentication, CifsProtocol, Mode, MountedConnectorMode } from '../../../models/connector';
import { OperationType, ConnectorType, Endpoint, HelpIndex, MessageType, UpdateStatusType } from '../../../models/enums';
import { CommonService } from '../../../services/common/common.service';
import { RestService } from '../../../services/rest/rest.service';
import { GenericTreeNode } from '../../generic-tree/generic-tree-node';
import { GenericTreeComponent } from '../../generic-tree/generic-tree.component';
import { ConnectorViewModel } from '../../../models/connector';
import { DrObject } from '../../../models/dr-object';
import { isUndefined } from '../../../models/utils';
import { NgForm } from '@angular/forms';

@Component({
    standalone: false,
    selector: 'res-connectors-modal',
    templateUrl: './connectors-modal.component.html',
    styleUrls: ['./connectors-modal.component.scss'],
    encapsulation: ViewEncapsulation.None
})

export class ConnectorsModalComponent extends BaseModalComponent implements OnInit {
    @ViewChild(GenericTreeComponent) tree: GenericTreeComponent;
    @ViewChild('successPop') successPop: PopoverDirective;
    @ViewChild('noSharesFoundPop') noSharesFoundPop: PopoverDirective;
    @ViewChild('validateSuccessPopup') validateSuccessPopup: PopoverDirective;
    @ViewChild('errorPopover') errorPopover: PopoverDirective;
    @ViewChild('contentForm') contentForm: NgForm;
    @HostListener('window:keydown.enter', ['$event'])
    onEnter(event: KeyboardEvent): void {
        if ((event.target as any)?.id.localeCompare('nfs-server-input') == 0 || (event.target as any)?.id.localeCompare('cifs-server-input') == 0) {
            if (this.connectorType === ConnectorType.NFS) {
                document.getElementById('nfs-server-input').blur();
            } else if (this.connectorType === ConnectorType.CIFS) {
                document.getElementById('cifs-server-input').blur();
            }
        }
    }

    ConnectorType = ConnectorType;

    name: string;
    originalName: string;
    description: string;
    originalDescription: string;

    modalTitle = '';
    modalAutomationId = 'connector';
    secondaryButtonLabel = 'Cancel';
    primaryButtonLabel = '';
    modalClass = 'connectors-modal';
    hideValidateButton = false;
    validateButtonLabel = 'Validate';
    modalHelpIndex = HelpIndex.create_connector_dlg;

    connectorOperationType: OperationType;
    connectorType: ConnectorType;

    drObject: ConnectorViewModel;
    server: string;

    formValid = false;

    selectedPath = '';
    selectedOriginalPath: string;
    originalRemotePath: string;

    // Cifs
    selectedOriginalCifsUsername: string;
    selectedOriginalCifsPassword: string;
    selectedOriginalCifsWorkgroupOrDomain: string;
    selectedOriginalCifsAuthentication: string;
    selectedOriginalProtocol: string;
    selectedOriginalLegacyMode: boolean;

    // MS (Exchange or Sharepoint)
    selectedOriginalMSServer: string;
    selectedOriginalMSUsername: string;
    selectedOriginalMSPassword: string;
    selectedOriginalMSDomain: string;
    selectedOriginalMSOffice365: boolean;
    selectedOriginalMSTenantDomain: string;
    selectedOriginalMSApplicationId: string;
    selectedOriginalMSApplicationSecret: string;
    selectedOriginalMSRefreshToken: string;

    isBusy = false;
    isBusyMessage = '';

    // dirty flags
    isServerDirty = false;
    isNameDirty = false;
    isDescriptionDirty = false;
    isPathDirty = false;
    hasGetMountsBeenCalled = false;

    nodes = new Array<GenericTreeNode>();
    filteredNodes = new Array<GenericTreeNode>();

    hasShares = false;
    mount = '';
    isLoadingMounts = false;
    isLoadingMountsMsg = '';
    connectorHandle = '';
    isEditConnector = false;
    validateNameError: string;
    validationError = false;
    showPathChangedWarning = false;

    // connector type fields
    // NFS and CIFS
    mode: Mode = Mode.READ;

    // NFS
    nfsIsPanFS = false;

    // CIFS
    cifsProtocol: CifsProtocol = CifsProtocol.FQDN_OR_IP;
    cifsAuthentications = CifsAuthentication;
    cifsAuthentication: CifsAuthentication = CifsAuthentication.NTLM;
    cifsUsername: string;
    cifsPassword: string;
    cifsWorkgroupOrDomain: string;
    cifsLegacyMode = false;

    // MS (EXCHANGE or SHAREPOINT)
    msOffice365: boolean;
    msUsername: string;
    msPassword: string;
    msDomain: string;
    msTenantDomain: string;
    msApplicationId: string;
    msApplicationSecret: string;
    msRefreshToken: string;
    msLastUpdatedDateTime: string = '';
    msLastUpdatedDateMiliseconds = 0;

    constructor(
        private modalService: BsModalService,
        private commonService: CommonService,
        private modalRef: BsModalRef,
        private restService: RestService
    ) {
        super(modalService, modalRef);
    }

    ngOnInit(): void {
        this.connectorOperationType = <OperationType>this.modalData.operationType;
        this.connectorType = <ConnectorType>this.modalData.connectorType;

        this.drObject = new ConnectorViewModel(this.modalData);

        this.modalTitle = this.connectorOperationType === OperationType.new ? 'New Connector' : 'Edit Connector';
        if (this.connectorOperationType === OperationType.edit) {
            this.isEditConnector = true;
            this.connectorHandle = this.modalData.connectorItem.handle;
            this.name = this.originalName = this.modalData.connectorItem.name;
            this.description = this.originalDescription = this.modalData.connectorItem.description;
            this.connectorType = this.modalData.connectorItem.type;
            this.formValid = true;
            this.connectorType = (this.connectorType === 'Sharepoint' as ConnectorType) ? ConnectorType.MSSharePoint : this.connectorType;
            this.loadConnector();
        } else {
            this.setEmptyValues();
        }
        this.primaryButtonLabel = this.connectorOperationType === OperationType.new ? 'Create Connector' : 'Save Changes';
    }

    isPrimaryButtonEnabled(): boolean {
        let enabled = false;
        if (!this.isBusy) {
            if (this.isModalDirty()) {
                if (this.connectorType === ConnectorType.NFS) {
                    enabled = this.selectedPath && this.isFormValid() && this.isNfsValid();
                }
                if (this.connectorType === ConnectorType.CIFS) {
                    enabled = this.selectedPath && this.isFormValid() && (this.hasGetMountsBeenCalled === true) && this.isCifsValid();
                }
                if (this.connectorType === ConnectorType.MSExchange || this.connectorType === ConnectorType.MSSharePoint || this.connectorType === ConnectorType.GoogleWorkspace) {
                    enabled = this.isFormValid() && this.isMSValid();
                }
            }
        }
        return enabled;
    }

    loadConnector(): void {
        this.validateNameError = null;
        if (this.connectorHandle) {
            let endpoint: Endpoint;
            switch (this.connectorType) {
                case ConnectorType.NFS: {
                    this.isBusyMessage = 'Loading NFS Connector...';
                    endpoint = Endpoint.connectorManager_getNFSConnector;
                    break;
                }
                case ConnectorType.CIFS: {
                    this.isBusyMessage = 'Loading CIFS Connector...';
                    endpoint = Endpoint.connectorManager_getCIFSConnector;
                    break;
                }
                case ConnectorType.MSExchange: {
                    this.isBusyMessage = 'Loading MS Exchange Connector...';
                    endpoint = Endpoint.connectorManager_getExchangeConnector;
                    break;
                }
                case ConnectorType.MSSharePoint: {
                    this.isBusyMessage = 'Loading Sharepoint Connector...';
                    endpoint = Endpoint.connectorManager_getSharepointConnector;
                    break;
                }
                case ConnectorType.GoogleWorkspace: {
                    this.isBusyMessage = 'Loading Google Workspace Connector...';
                    endpoint = Endpoint.connectorManager_getGoogleWorkspaceConnector;
                    break;
                }
            }
            if (endpoint) {
                this.isBusy = true;
                this.restService.post(endpoint, { handle: this.connectorHandle }).then(response => {
                    this.isBusy = false;
                    if (response && response.connector) {
                        switch (this.connectorType) {
                            case ConnectorType.NFS: {
                                this.mode = response.connector.mode;
                                this.server = response.connector.networkId;
                                this.description = response.connector.description;
                                this.selectedPath = this.selectedOriginalPath = this.originalRemotePath = response.connector.remotePath;
                                this.nfsIsPanFS = response.connector.mountedConnectorMode === MountedConnectorMode.PANFS;
                                this.getMounts();
                                break;
                            }
                            case ConnectorType.CIFS: {
                                this.mode = response.connector.mode;
                                this.server = response.connector.networkId;
                                this.description = response.connector.description;
                                this.selectedPath = this.selectedOriginalPath = this.originalRemotePath = response.connector.remotePath;
                                this.cifsUsername = this.selectedOriginalCifsUsername = response.connector.userName;
                                this.cifsPassword = this.selectedOriginalCifsPassword = response.connector.password;
                                this.cifsWorkgroupOrDomain = this.selectedOriginalCifsWorkgroupOrDomain = response.connector.workgroupOrDomain;
                                this.cifsAuthentication = this.selectedOriginalCifsAuthentication = response.connector.authenticationType;
                                this.cifsProtocol = this.selectedOriginalProtocol = response.connector.protocol;
                                this.cifsLegacyMode = this.selectedOriginalLegacyMode = response.connector.supportSMB1;
                                this.getMounts();
                                break;
                            }
                            case ConnectorType.MSExchange:
                            case ConnectorType.MSSharePoint:
                            case ConnectorType.GoogleWorkspace: {
                                this.mode = response.connector.readOnly;
                                this.server = this.selectedOriginalMSServer = response.connector.contentUrl;
                                this.description = response.connector.description;
                                this.msUsername = this.selectedOriginalMSUsername = this.connectorType === ConnectorType.MSSharePoint ? response.connector.userName : response.connector.username;
                                this.msPassword = this.selectedOriginalMSPassword = response.connector.password;
                                this.msDomain = this.selectedOriginalMSDomain = response.connector.domainController;
                                this.msOffice365 = this.selectedOriginalMSOffice365 =  this.connectorType === ConnectorType.GoogleWorkspace ? true : response.connector.azureADEnabled;
                                this.msTenantDomain = this.selectedOriginalMSTenantDomain = response.connector.tenantDomain;
                                this.msApplicationId = this.selectedOriginalMSApplicationId = this.connectorType === ConnectorType.GoogleWorkspace ? response.connector.serviceAccountEmail : response.connector.applicationId;
                                this.msApplicationSecret = this.selectedOriginalMSApplicationSecret = this.connectorType === ConnectorType.GoogleWorkspace ? response.connector.serviceAccountKey : response.connector.applicationSecret;
                                this.msRefreshToken = this.selectedOriginalMSRefreshToken = response.connector.refreshToken;
                                this.msLastUpdatedDateTime = response.connector.lastUpdateFormatted;
                                this.msLastUpdatedDateMiliseconds = response.connector.lastUpdated;
                                break;
                            }
                        }
                    }
                }, error => {
                    this.isBusy = false;
                });
            }
        }
    }

    setEmptyValues(): void {
        this.server = this.selectedOriginalMSServer = null;
        this.msUsername = this.selectedOriginalMSUsername = null;
        this.msPassword = this.selectedOriginalMSPassword = null;
        this.msDomain = this.selectedOriginalMSDomain = null;
        this.msOffice365 = this.selectedOriginalMSOffice365 = false;
        this.msTenantDomain = this.selectedOriginalMSTenantDomain = null;
        this.msApplicationId = this.selectedOriginalMSApplicationId = null;
        this.msApplicationSecret = this.selectedOriginalMSApplicationSecret = null;
        this.msRefreshToken = this.selectedOriginalMSRefreshToken = null;
    }

    onNameFocusLost(): void {
        this.name = this.name.trim();
        if (!isUndefined(this.name) && (this.validateNameError === null || isUndefined(this.validateNameError))) {
            this.isNameDirty = DrObject.isValueDirty(this.name, this.originalName);
            if (this.isNameDirty) {
                this.formValid = true;
                this.restService.post(Endpoint.viewManager_validateName, {
                    name: this.name,
                    objectType: this.drObject.objectType
                }).then(response => {
                    if (response.valid === false) {
                        this.validateNameError = response.reason;
                        this.errorPopover.show();
                        this.formValid = false;
                    }
                });
            }
        }
    }

    onDescriptionFocusLost(): void {
        if (!isUndefined(this.description)) {
            this.description = this.description.trim();
            this.isDescriptionDirty = DrObject.isValueDirty(this.description, this.originalDescription);
        }
    }

    ok(): void {
        let endpoint: Endpoint;
        let request: any;
        switch (this.connectorType) {
            case ConnectorType.NFS: {
                if (this.connectorOperationType === OperationType.new) {
                    this.isBusyMessage = 'Creating NFS Connector...';
                    endpoint = Endpoint.orgManager_createNFSConnector;
                    request = {
                        description: this.description,
                        mountedConnectorMode: this.nfsIsPanFS ? MountedConnectorMode.PANFS : MountedConnectorMode.CLASSIC,
                        name: this.name,
                        readOnly: this.mode === Mode.READ ? true : false,
                        remoteHost: this.server,
                        remotePath: this.selectedPath,
                    };
                } else if (this.connectorOperationType === OperationType.edit) {
                    this.isBusyMessage = 'Updating NFS Connector...';
                    endpoint = Endpoint.connectorManager_updateNFSConnector;
                    this.showPathChangedWarning = this.modalData?.connectorItem?.remotePath !== this.selectedPath;
                    request = {
                        handle: this.connectorHandle,
                        description: this.description,
                        mountedConnectorMode: this.nfsIsPanFS ? MountedConnectorMode.PANFS : MountedConnectorMode.CLASSIC,
                        remoteHost: this.server,
                        remotePath: this.selectedPath
                    };
                }
                break;
            }
            case ConnectorType.CIFS: {
                if (this.connectorOperationType === OperationType.new) {
                    this.isBusyMessage = 'Creating CIFS Connector...';
                    endpoint = Endpoint.orgManager_createCIFSConnector;
                    request = {
                        description: this.description,
                        name: this.name,
                        readOnly: this.mode === Mode.READ ? true : false,
                        remoteHost: this.server,
                        remotePath: this.selectedPath,
                        protocol: this.cifsProtocol,
                        authenticationType: this.cifsAuthentication,
                        userName: this.cifsUsername,
                        password: this.cifsPassword,
                        workgroupOrDomain: this.cifsWorkgroupOrDomain,
                        supportSMB1: this.cifsLegacyMode
                    };
                } else if (this.connectorOperationType === OperationType.edit) {
                    this.isBusyMessage = 'Updating CIFS Connector...';
                    endpoint = Endpoint.connectorManager_updateCIFSConnector;
                    this.showPathChangedWarning = this.modalData?.connectorItem?.remotePath !== '/' + this.selectedPath;
                    request = {
                        handle: this.connectorHandle,
                        description: this.description,
                        remoteHost: this.server,
                        remotePath: this.selectedPath,
                        protocol: this.cifsProtocol,
                        authenticationType: this.cifsAuthentication,
                        userName: this.cifsUsername,
                        password: this.cifsPassword,
                        workgroupOrDomain: this.cifsWorkgroupOrDomain,
                        supportSMB1: this.cifsLegacyMode
                    };
                }
                break;
            }
            case ConnectorType.MSExchange: {
                request = {
                    description: this.description,
                    name: this.name,
                    username: this.msUsername,
                    password: this.msPassword,
                    contentUrl: this.server,
                    domainController: this.msDomain,
                    azureADEnabled: this.msOffice365,
                    tenantDomain: this.msOffice365 ? this.msTenantDomain : null,
                    applicationId: this.msOffice365 ? this.msApplicationId : null,
                    applicationSecret: this.msOffice365 ? this.msApplicationSecret : null,
                    refreshToken: this.msOffice365 ? this.msRefreshToken : null
                };
                if (this.connectorOperationType === OperationType.new) {
                    this.isBusyMessage = 'Creating MS Exchange Connector...';
                    endpoint = Endpoint.orgManager_createExchangeConnector;
                    request.readOnly = true;
                } else if (this.connectorOperationType === OperationType.edit) {
                    this.isBusyMessage = 'Updating MS Exchange Connector...';
                    endpoint = Endpoint.connectorManager_updateExchangeConnector;
                    request.handle = this.connectorHandle;
                }
                break;
            }
            case ConnectorType.MSSharePoint: {
                request = {
                    description: this.description,
                    name: this.name,
                    username: this.msUsername,
                    password: this.msPassword,
                    contentUrl: this.server,
                    domainController: this.msDomain,
                    azureADEnabled: this.msOffice365,
                    tenantDomain: this.msOffice365 ? this.msTenantDomain : null,
                    applicationId: this.msOffice365 ? this.msApplicationId : null,
                    applicationSecret: this.msOffice365 ? this.msApplicationSecret : null,
                    refreshToken: this.msOffice365 ? this.msRefreshToken : null,
                    oneDriveTenantDomain: this.msOffice365 ? this.msTenantDomain : null,
                    oneDriveApplicationId: this.msOffice365 ? this.msApplicationId : null,
                    oneDriveApplicationSecret: this.msOffice365 ? this.msApplicationSecret : null,
                    oneDriveRefreshToken: this.msOffice365 ? this.msRefreshToken : null,
                };
                if (this.connectorOperationType === OperationType.new) {
                    this.isBusyMessage = 'Creating Sharepoint Connector...';
                    endpoint = Endpoint.orgManager_createSharepointConnector;
                } else if (this.connectorOperationType === OperationType.edit) {
                    this.isBusyMessage = 'Updating Sharepoint Connector...';
                    endpoint = Endpoint.connectorManager_updateSharepointConnector;
                    request.handle = this.connectorHandle;
                }
                break;
            }
             case ConnectorType.GoogleWorkspace: {
                request = {
                    description: this.description,
                    name: this.name,
                    serviceAccountEmail: this.msApplicationId,
                    serviceAccountKey: this.msApplicationSecret,
                    refreshToken: this.msRefreshToken,
                    tenantDomain: this.msTenantDomain
                };
                if (this.connectorOperationType === OperationType.new) {
                    this.isBusyMessage = 'Creating Google Workspace Connector...';
                    endpoint = Endpoint.orgManager_createGoogleWorkspaceConnector;
                } else if (this.connectorOperationType === OperationType.edit) {
                    this.isBusyMessage = 'Updating Google Workspace Connector...';
                    endpoint = Endpoint.connectorManager_updateGoogleWorkspaceConnector;
                    request.handle = this.connectorHandle;
                }
                break;
            }
        }
        if (endpoint && request) {
            this.isBusy = true;
            if (this.showPathChangedWarning) {
                this.commonService.openConfirmationWarningModal('Changing the path will require a system restart to propagate the change. Please contact your System Administrator.', 'Warning', null, 'Close');
            }
            this.restService.post(endpoint, request).then(response => {
                this.isBusy = false;
                if (response) {
                    this.commonService.sendMessage(MessageType.entityChanged, UpdateStatusType.CONNECTOR);
                    this.closeModal(true, response.connector);
                    this.resetIsDirtyFlags();
                }
            }, () => {
                this.isBusy = false;
            });
        }
        this.validateNameError = null;
    }

    setType(type): void {
        this.connectorType = type;
        this.server = null;
        this.selectedPath = null;
        this.filteredNodes = this.nodes = [];
        this.setEmptyValues();
        if(this.connectorType === ConnectorType.GoogleWorkspace){
            this.msOffice365 = true;
        }
        if (this.tree) {
            this.tree.setData(this.filteredNodes);
        }
    }

    loadNodeDataCallback = (node: GenericTreeNode) => {
        if (node && this.filteredNodes) {
            if (node.icon === 'fa-share-alt') {
                this.mount = node.data;
            }
            const request: any = {
                connectorType: this.connectorType,
                connectorName: this.name || 'exploreConnectorName',
                remoteHost: this.server,
                remotePath: this.mount,
                organizationName: this.commonService.currentUser.customerName,
                parentPath: {
                    name: node.name,
                    handle: node.data.handle ? node.data.handle : '',
                    leaf: node.data.leaf ? node.data.leaf : false,
                    type: node.data.type ? node.data.type : null
                },
                connectorHandle: !this.isEditConnector && this.connectorHandle ? this.connectorHandle : undefined
            };
            switch (this.connectorType) {
                case ConnectorType.NFS: {
                    break;
                }
                case ConnectorType.CIFS: {
                    request.userName = this.cifsUsername;
                    request.password = this.cifsPassword;
                    request.workgroupOrDomain = this.cifsWorkgroupOrDomain;
                    request.protocol = this.cifsProtocol;
                    request.authenticationType = this.cifsAuthentication;
                    request.supportSMB1 = this.cifsLegacyMode;
                    break;
                }
            }
            node.isLoading = true;
            this.restService.post(Endpoint.connectorManager_exploreConnector, request).then(response => {
                node.isLoading = false;
                if (response && response.paths) {
                    response.paths.forEach((el, index) => {
                        if (el.name !== '.' && el.name !== '..') {
                            node.children.push(new GenericTreeNode({
                                name: el.name,
                                icon: 'fa-folder',
                                iconExpanded: 'fa-folder-open',
                                iconColor: 'color-yellow-07',
                                isExpandable: !el.leaf,
                                isExpandedOnSelect: !el.leaf,
                                hasExpanderPadding: true,
                                data: el,
                                automationId: node.automationId + el.name + index
                            }));
                        }
                    });
                    if (node.children.length === 0) {
                        node.isExpandable = false;
                        node.iconExpanded = null;
                    }
                }
                this.tree.setData(this.filteredNodes);
                this.checkNodeToSelect(node);
            });
        }
    }

    checkNodeToSelect = (parent?: GenericTreeNode) => {
        if (this.selectedOriginalPath) {
            const nodeToSelect = this.tree.getNodeBy(n => n.name && (this.selectedOriginalPath.indexOf(n.name) === 0 ||
                this.selectedOriginalPath.indexOf('/' + n.name) === 0), parent);
            if (nodeToSelect) {
                this.selectedOriginalPath = this.selectedOriginalPath.substr(nodeToSelect.name.length + this.selectedOriginalPath.indexOf(nodeToSelect.name));
                if (this.selectedOriginalPath.length === 0) {
                    this.selectedOriginalPath = null;
                }
                this.tree.selectNode(nodeToSelect);
            }
        } else if (parent) {
            this.tree.selectNode(parent);
        }
    }

    selectedNodeChangedCallback = (node: GenericTreeNode) => {
        if (node) {
            this.setPath(node.data);
        }
        if (this.successPop && this.successPop.isOpen) {
            this.successPop.hide();
        }
    }

    filterData(str: string): void {
        if (str) {
            this.filteredNodes = this.nodes.filter(li => {
                return li.name.toLowerCase().includes(str);
            });
        } else {
            this.filteredNodes = this.nodes;
        }
        this.tree.setData(this.filteredNodes);
    }

    getMounts(): void {
        if (this.server === '') {
            this.nodes.length = 0;
            this.filteredNodes.length = 0;
            this.tree.setData(this.filteredNodes);
        } else if (this.server) {
            const timeoutSeconds = 120,
                remoteHost = this.server ? this.server : '';
            this.isLoadingMountsMsg = 'Loading Mounts...';
            this.isLoadingMounts = true;
            this.filteredNodes = [];
            this.nodes = [];
            let endpoint: Endpoint;
            let request: any;
            switch (this.connectorType) {
                case ConnectorType.NFS: {
                    endpoint = Endpoint.orgManager_getNfsMounts;
                    request = {
                        remoteHost: remoteHost,
                        timeoutSeconds: timeoutSeconds
                    };
                    break;
                }
                case ConnectorType.CIFS: {
                    endpoint = Endpoint.orgManager_getCifsMounts;
                    request = {
                        remoteHost: remoteHost,
                        protocol: this.cifsProtocol,
                        password: this.cifsPassword,
                        userName: this.cifsUsername,
                        workgroupOrDomain: this.cifsWorkgroupOrDomain,
                        authenticationType: this.cifsAuthentication,
                        timeoutSeconds: timeoutSeconds,
                    };
                    break;
                }
            }
            if (endpoint && request) {
                this.restService.post(endpoint, request, false).then(response => {
                    this.isLoadingMounts = false;
                    if (response.mounts.length === 0) {
                        this.noSharesFoundPop.show();
                    } else {
                        this.noSharesFoundPop.hide();
                    }
                    if (response.mounts && response.mounts.length > 0) {
                        response.mounts.forEach((mount, index) => {
                            if (mount !== '.' && mount !== '..') {
                                this.nodes.push(new GenericTreeNode({
                                    name: mount,
                                    icon: 'fa-share-alt',
                                    iconExpanded: 'fa-share-alt',
                                    iconColor: 'color-blue-01',
                                    isExpandable: true,
                                    isExpandedOnSelect: true,
                                    hasExpanderPadding: true,
                                    data: mount,
                                    automationId: 'path-' + index
                                }));
                            }
                            this.filteredNodes = this.nodes;
                        });
                    }
                    this.tree.setData(this.filteredNodes);
                    this.checkNodeToSelect();
                    this.hasGetMountsBeenCalled = true;
                }, error => {
                    this.isLoadingMounts = false;
                    this.noSharesFoundPop.show();
                });
            }
            this.hasShares = this.filteredNodes.length > 0 ? true : false;
        } else {
            this.noSharesFoundPop.hide();
        }
    }

    validate(): void {
        let endpoint: Endpoint;
        let request: any;
        switch (this.connectorType) {
            case ConnectorType.NFS: {
                this.isBusyMessage = 'Validating NFS Connector...';
                endpoint = Endpoint.connectorManager_validateNFSConnector;
                request = {
                    description: this.description,
                    name: this.name,
                    remoteHost: this.server,
                    remotePath: this.selectedPath,
                    edit: true,
                    readOnly: this.mode === Mode.READ ? true : false
                };
                break;
            }
            case ConnectorType.CIFS: {
                this.isBusyMessage = 'Validating CIFS Connector...';
                endpoint = Endpoint.connectorManager_validateCIFSConnector;
                request = {
                    description: this.description,
                    name: this.name,
                    remoteHost: this.server,
                    remotePath: this.selectedPath,
                    edit: true,
                    readOnly: this.mode === Mode.READ ? true : false,
                    protocol: this.cifsProtocol,
                    authenticationType: this.cifsAuthentication,
                    userName: this.cifsUsername,
                    password: this.cifsPassword,
                    workgroupOrDomain: this.cifsWorkgroupOrDomain,
                    supportSMB1: this.cifsLegacyMode
                };
                break;
            }
            case ConnectorType.MSExchange: {
                this.isBusyMessage = 'Validating MSExchange Connector...';
                endpoint = Endpoint.connectorManager_validateExchangeConnector;
                request = {
                    description: this.description,
                    name: this.name,
                    username: !this.msOffice365 ? this.msUsername : null,
                    password: !this.msOffice365 ? this.msPassword : null,
                    contentUrl: this.server,
                    domainController: !this.msOffice365 ? this.msDomain : null,
                    azureADEnabled: this.msOffice365,
                    tenantDomain: this.msOffice365 ? this.msTenantDomain : null,
                    applicationId: this.msOffice365 ? this.msApplicationId : null,
                    applicationSecret: this.msOffice365 ? this.msApplicationSecret : null
                };
                break;
            }
            case ConnectorType.MSSharePoint: {
                this.isBusyMessage = 'Validating Sharepoint Connector...';
                endpoint = Endpoint.connectorManager_validateSharepointConnector;
                request = {
                    description: this.description,
                    name: this.name,
                    username: !this.msOffice365 ? this.msUsername : null,
                    password: !this.msOffice365 ? this.msPassword : null,
                    contentUrl: this.server,
                    domainController: !this.msOffice365 ? this.msDomain : null,
                    azureADEnabled: this.msOffice365,
                    tenantDomain: this.msOffice365 ? this.msTenantDomain : null,
                    applicationId: this.msOffice365 ? this.msApplicationId : null,
                    applicationSecret: this.msOffice365 ? this.msApplicationSecret : null,
                    refreshToken: this.msOffice365 ? this.msRefreshToken : null,
                    oneDriveTenantDomain: this.msOffice365 ? this.msTenantDomain : null,
                    oneDriveApplicationId: this.msOffice365 ? this.msApplicationId : null,
                    oneDriveApplicationSecret: this.msOffice365 ? this.msApplicationSecret : null,
                    oneDriveRefreshToken: this.msOffice365 ? this.msRefreshToken : null,
                };
                break;
            }
             case ConnectorType.GoogleWorkspace: {
                this.isBusyMessage = 'Validating Google Workspace Connector...';
                endpoint = Endpoint.connectorManager_validateGoogleWorkspaceConnector;
                request = {
                    description: this.description,
                    name: this.name,
                    serviceAccountEmail: this.msApplicationId,
                    serviceAccountKey: this.msApplicationSecret,
                    refreshToken: this.msRefreshToken,
                    tenantDomain: this.msTenantDomain
                };
                break;
            }
        }
        if (endpoint && request) {
            this.isBusy = true;
            this.restService.post(endpoint, request).then(response => {
                this.isBusy = false;
                this.validationError = false;
                if (response && response.status === 'SUCCESS') {
                    this.successPop.show();
                }
            }, error => {
                this.isBusy = false;
                this.validationError = false;
            });
        }
    }

    isValidateEnabled(): boolean {
        switch (this.connectorType) {
            case ConnectorType.NFS: {
                return !!this.server && !!this.selectedPath && !this.isBusy && this.formValid;
            }
            case ConnectorType.CIFS: {
                return !!this.server && !!this.selectedPath && !this.isBusy && this.formValid && this.isCifsValid();
            }
            case ConnectorType.MSExchange:
            case ConnectorType.MSSharePoint:
            case ConnectorType.GoogleWorkspace: {
                return !this.isBusy && this.formValid && this.isMSValid();
            }
        }
    }

    validateCharacters(name: string) {
        this.errorPopover.hide();
        this.formValid = true;
        const trimmedName = DrObject.trimLeadingWhiteSpaces(name);
        if (trimmedName !== '') {
            this.validateNameError = this.drObject.uiValidateName(trimmedName);
            if (this.validateNameError) {
                this.errorPopover.show();
                this.formValid = false;
            }
        }
    }

    onNameChanged(): void {
        this.isNameDirty = false;
        if (DrObject.isValueDirty(this.name, this.originalName)) {
            this.validateCharacters(this.name);
            this.isNameDirty = true;
        } else {
            this.isNameDirty = false;
        }
    }

    onDescriptionChanged(): void {
        this.isDescriptionDirty = false;
        if (DrObject.isValueDirty(this.description, this.originalDescription)) {
            this.isDescriptionDirty = true;
        } else {
            this.isDescriptionDirty = false;
        }
    }

    onMSPasswordChanged(): void {
        this.msPassword = this.contentForm.value.msPasswordInput ? this.contentForm.value.msPasswordInput : this.selectedOriginalMSPassword;
    }

    onServerChanged(): void {
        this.isServerDirty = true;
        this.hasGetMountsBeenCalled = false;
        this.selectedPath = '';
    }

    onLegacyModeChange(): void {
        this.getMounts();
    }

    resetIsDirtyFlags() {
        this.isNameDirty = false;
        this.isDescriptionDirty = false;
        this.isServerDirty = false;
        this.isPathDirty = false;
    }

    hasValidateNameErrors(): boolean {
        let hasErrors = false;
        if ((!isUndefined(this.name) && this.name !== null) && (!isUndefined(this.validateNameError) && this.validateNameError !== null)) {
            hasErrors = true;
        }
        return hasErrors;
    }

    isFormValid(): boolean {
        let isValid = false;
        if (this.name && this.connectorType &&
            (this.server || ((this.connectorType === ConnectorType.MSExchange || this.connectorType === ConnectorType.MSSharePoint || this.connectorType === ConnectorType.GoogleWorkspace) && this.msOffice365)) &&
            (this.hasValidateNameErrors() === false) && !this.validationError) {
            isValid = true;
        }
        return isValid;
    }

    isConnectorTypeDirty(): boolean {
        let isConnectoryTypeDirty = false;
        if (!this.isEditConnector && (this.connectorType !== ConnectorType.NFS)) {
            isConnectoryTypeDirty = true;
        }
        return isConnectoryTypeDirty;
    }

    isModalDirty(): boolean {
        let isModalDirty = false;
        if (this.isNameDirty || this.isDescriptionDirty || this.isPathDirty || this.isConnectorTypeDirty() || this.isServerDirty ||
            (this.isServerDirty && (this.hasGetMountsBeenCalled === false)) || this.isCifModalDirty() || this.isExchangeModalDirty() || this.isSharepointModalDirty() || this.isGoogleWorkspaceModalDirty()) {
            isModalDirty = true;
        }
        return isModalDirty;
    }

    isCifModalDirty(): boolean {
        if (this.connectorType === ConnectorType.CIFS) {
            return DrObject.isValueDirty(this.cifsProtocol, this.selectedOriginalProtocol) || DrObject.isValueDirty(this.cifsUsername, this.selectedOriginalCifsUsername) ||
                DrObject.isValueDirty(this.cifsPassword, this.selectedOriginalCifsPassword) || DrObject.isValueDirty(this.cifsWorkgroupOrDomain, this.selectedOriginalCifsWorkgroupOrDomain) ||
                DrObject.isValueDirty(this.cifsAuthentication, this.selectedOriginalCifsAuthentication) || (this.cifsLegacyMode !== this.selectedOriginalLegacyMode);
        } else {
            return false;
        }
    }

    isExchangeModalDirty(): boolean {
        if (this.connectorType === ConnectorType.MSExchange) {
            return DrObject.isValueDirty(this.server, this.selectedOriginalMSServer) || DrObject.isValueDirty(this.msUsername, this.selectedOriginalMSUsername) ||
                DrObject.isValueDirty(this.msPassword, this.selectedOriginalMSPassword) || DrObject.isValueDirty(this.msDomain, this.selectedOriginalMSDomain) ||
                this.msOffice365 !== this.selectedOriginalMSOffice365 || DrObject.isValueDirty(this.msTenantDomain, this.selectedOriginalMSTenantDomain) ||
                DrObject.isValueDirty(this.msApplicationId, this.selectedOriginalMSApplicationId) ||
                DrObject.isValueDirty(this.msApplicationSecret, this.selectedOriginalMSApplicationSecret) ||
                DrObject.isValueDirty(this.msRefreshToken, this.selectedOriginalMSRefreshToken);

        } else {
            return false;
        }
    }

    isSharepointModalDirty(): boolean {
        if (this.connectorType === ConnectorType.MSSharePoint) {
            return DrObject.isValueDirty(this.server, this.selectedOriginalMSServer) || DrObject.isValueDirty(this.msUsername, this.selectedOriginalMSUsername) ||
                DrObject.isValueDirty(this.msPassword, this.selectedOriginalMSPassword) || DrObject.isValueDirty(this.msDomain, this.selectedOriginalMSDomain) ||
                this.msOffice365 !== this.selectedOriginalMSOffice365 || DrObject.isValueDirty(this.msTenantDomain, this.selectedOriginalMSTenantDomain) ||
                DrObject.isValueDirty(this.msApplicationId, this.selectedOriginalMSApplicationId) ||
                DrObject.isValueDirty(this.msApplicationSecret, this.selectedOriginalMSApplicationSecret) ||
                DrObject.isValueDirty(this.msRefreshToken, this.selectedOriginalMSRefreshToken);

        } else {
            return false;
        }
    }

    isGoogleWorkspaceModalDirty(): boolean {
         if (this.connectorType === ConnectorType.GoogleWorkspace) {
            return DrObject.isValueDirty(this.msTenantDomain, this.selectedOriginalMSTenantDomain) ||
                DrObject.isValueDirty(this.msApplicationId, this.selectedOriginalMSApplicationId) ||
                DrObject.isValueDirty(this.msApplicationSecret, this.selectedOriginalMSApplicationSecret) ||
                DrObject.isValueDirty(this.msRefreshToken, this.selectedOriginalMSRefreshToken);

        } else {
            return false;
        }
    }

    setPath(selPath: any): void {
        this.isPathDirty = false;
        this.selectedPath = selPath.handle ? this.mount + selPath.handle : selPath;
        if (DrObject.isValueDirty(this.selectedPath, this.originalRemotePath)) {
            this.isPathDirty = true;
        }
    }

    onCifsAuthenticationChange(): void {
        if (this.cifsAuthentication === CifsAuthentication.NONE) {
            this.cifsUsername = '';
            this.cifsPassword = '';
            this.cifsWorkgroupOrDomain = '';
        }

        this.filteredNodes = this.nodes = [];
        this.tree.setData(this.filteredNodes);
        this.selectedOriginalPath = this.selectedPath;
        this.getMounts();
    }

    isNfsValid(): boolean {
        return (this.hasGetMountsBeenCalled === true);
    }

    isCifsValid(): boolean {
        return (this.cifsAuthentication === CifsAuthentication.NONE) || Boolean(this.cifsUsername && this.cifsPassword && this.cifsWorkgroupOrDomain);
    }

    isMSValid(): boolean {
        return !this.msOffice365 ? Boolean(this.msUsername && this.msPassword && this.msDomain && this.server) :
            Boolean(this.msTenantDomain && this.msApplicationId &&
                this.msApplicationSecret);
    }
}
